import React, { useRef, useEffect, useState } from 'react';
import { 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  Type,
  Palette
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  rows?: number;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter text...',
  className = '',
  rows = 4
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Update the editor content when value prop changes
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const executeCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const formatText = (command: string) => {
    executeCommand(command);
  };

  const insertList = (ordered: boolean = false) => {
    const command = ordered ? 'insertOrderedList' : 'insertUnorderedList';
    executeCommand(command);
  };

  const alignText = (alignment: string) => {
    executeCommand('justify' + alignment);
  };

  const changeFontSize = (size: string) => {
    executeCommand('fontSize', size);
  };

  const changeColor = (color: string) => {
    executeCommand('foreColor', color);
  };

  interface ToolbarButton {
    icon: any;
    command?: string;
    title: string;
    action: (() => void) | null;
    isDropdown?: boolean;
    dropdown?: Array<{
      label: string;
      value: string;
      action: () => void;
    }>;
  }

  interface ToolbarGroup {
    group: string;
    buttons: ToolbarButton[];
  }

  const toolbarButtons: ToolbarGroup[] = [
    {
      group: 'format',
      buttons: [
        { 
          icon: Bold, 
          command: 'bold', 
          title: 'Bold (Ctrl+B)',
          action: () => formatText('bold')
        },
        { 
          icon: Italic, 
          command: 'italic', 
          title: 'Italic (Ctrl+I)',
          action: () => formatText('italic')
        },
        { 
          icon: Underline, 
          command: 'underline', 
          title: 'Underline (Ctrl+U)',
          action: () => formatText('underline')
        }
      ]
    },
    {
      group: 'alignment',
      buttons: [
        { 
          icon: AlignLeft, 
          command: 'justifyLeft', 
          title: 'Align Left',
          action: () => alignText('Left')
        },
        { 
          icon: AlignCenter, 
          command: 'justifyCenter', 
          title: 'Align Center',
          action: () => alignText('Center')
        },
        { 
          icon: AlignRight, 
          command: 'justifyRight', 
          title: 'Align Right',
          action: () => alignText('Right')
        }
      ]
    },
    {
      group: 'lists',
      buttons: [
        { 
          icon: List, 
          command: 'insertUnorderedList', 
          title: 'Bullet List',
          action: () => insertList(false)
        },
        { 
          icon: ListOrdered, 
          command: 'insertOrderedList', 
          title: 'Numbered List',
          action: () => insertList(true)
        }
      ]
    },
    {
      group: 'formatting',
      buttons: [
        {
          icon: Type,
          title: 'Font Size',
          action: null,
          isDropdown: true,
          dropdown: [
            { label: 'Small', value: '1', action: () => changeFontSize('1') },
            { label: 'Normal', value: '3', action: () => changeFontSize('3') },
            { label: 'Large', value: '5', action: () => changeFontSize('5') },
            { label: 'Extra Large', value: '7', action: () => changeFontSize('7') }
          ]
        }
      ]
    }
  ];

  const colors = [
    '#000000', '#333333', '#666666', '#999999',
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00',
    '#FF00FF', '#00FFFF', '#FFA500', '#800080'
  ];

  return (
    <div className={`rich-text-editor border border-gray-300 rounded-lg ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        {toolbarButtons.map((group, groupIndex) => (
          <div key={groupIndex} className="flex items-center gap-1">
            {group.buttons.map((button, buttonIndex) => (
              <div key={buttonIndex} className="relative">
                {button.isDropdown ? (
                  <div className="relative group">
                    <button
                      type="button"
                      className="p-1.5 text-gray-600 hover:bg-gray-200 rounded transition-colors"
                      title={button.title}
                    >
                      <button.icon className="w-4 h-4" />
                    </button>
                    <div className="absolute top-full left-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                      {button.dropdown?.map((item, itemIndex) => (
                        <button
                          key={itemIndex}
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg"
                          onClick={item.action}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="p-1.5 text-gray-600 hover:bg-gray-200 rounded transition-colors"
                    onClick={button.action || undefined}
                    title={button.title}
                  >
                    <button.icon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {groupIndex < toolbarButtons.length - 1 && (
              <div className="w-px h-6 bg-gray-300 mx-1" />
            )}
          </div>
        ))}
        
        {/* Color picker */}
        <div className="flex items-center gap-1 ml-2">
          <Palette className="w-4 h-4 text-gray-600" />
          <div className="flex gap-1">
            {colors.map((color, index) => (
              <button
                key={index}
                type="button"
                className="w-4 h-4 rounded border border-gray-300 hover:scale-110 transition-transform"
                style={{ backgroundColor: color }}
                onClick={() => changeColor(color)}
                title={`Text color: ${color}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className={`rich-text-content p-3 min-h-[${rows * 1.5}rem] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
          isFocused ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
        } ${!value && !isFocused ? 'text-gray-400' : ''}`}
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={(e) => {
          // Handle keyboard shortcuts
          if (e.ctrlKey || e.metaKey) {
            switch (e.key) {
              case 'b':
                e.preventDefault();
                formatText('bold');
                break;
              case 'i':
                e.preventDefault();
                formatText('italic');
                break;
              case 'u':
                e.preventDefault();
                formatText('underline');
                break;
            }
          }
        }}
        suppressContentEditableWarning={true}
        style={{ minHeight: `${rows * 1.5}rem` }}
        data-placeholder={placeholder}
      />

    </div>
  );
};

// CSS for the rich text editor (add to your CSS file)
export const richTextEditorStyles = `
  .rich-text-editor {
    position: relative;
  }
  
  .rich-text-editor [contenteditable]:empty:before {
    content: attr(data-placeholder);
    color: #9CA3AF;
    pointer-events: none;
  }
  
  .rich-text-editor [contenteditable]:focus:before {
    display: none;
  }
  
  .rich-text-editor ul,
  .rich-text-editor ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }
  
  .rich-text-editor p {
    margin: 0.5rem 0;
  }
  
  .rich-text-editor p:first-child {
    margin-top: 0;
  }
  
  .rich-text-editor p:last-child {
    margin-bottom: 0;
  }
`;
